import {doGet, doPost} from "./do-fetch"

/*
 * The AI personality a student is taught in — "Tutor Style" in the UI (142).
 *
 * The concept is not new: `user_profile.ai_personality_id` already decides which personality prompt
 * the question chat and the topic summary are built with (routes/mini.js), and the Welcome flow and
 * the profile screen already set it. This module only gives the dashboard the same reach, and puts
 * the fetch/save pair in one place instead of a third copy of it.
 *
 * NOTHING HERE CHANGES WHAT THE MODEL IS SENT. The personality is resolved server-side on every AI
 * call from the profile row, so a change takes effect on the next question or summary with no
 * client involvement at all.
 */

/*
 * The label the picker is presented under. "AI personality" is the database's word for it and the
 * admin screen's; a student is choosing how they want to be taught.
 */
const TUTOR_STYLE_LABEL = 'Tutor Style'

// id, name and description — the route deliberately omits the prompt text
const fetchTutorStyles = async () => {
	const response = await doGet('/mini/ais')
	if (!response.ok) throw new Error(`Could not load tutor styles (${response.status}).`)
	return await response.json()
}

/*
 * Saves the choice to the student's profile.
 *
 * POSTs only `ai_personality_id`. updateUserProfile writes `{first_name, last_name,
 * ai_personality_id}` as one object, so any field omitted here is written as undefined — which
 * Supabase's update treats as "leave alone" rather than as null. Confirmed against the service:
 * this cannot blank a name.
 */
const saveTutorStyle = async (aiPersonalityId) => {
	const response = await doPost('/mini/user/profile', {
		body: JSON.stringify({ai_personality_id: aiPersonalityId}),
	})
	if (!response.ok) throw new Error(`Could not save your tutor style (${response.status}).`)

	const result = await response.json()
	if (result && result.success === false) throw new Error('Could not save your tutor style.')

	/*
	 * Returns ONE profile row, whatever the endpoint hands back.
	 *
	 * `POST /mini/user/profile` replies `{success, userProfile}` where userProfile is an ARRAY —
	 * updateUserProfile ends in `.update(...).select('*')`, and supabase-js resolves that to a list
	 * even for a single-row match.
	 *
	 * Unwrapping it here is not tidiness. A caller that sets the array as its profile state gets an
	 * object with no `welcomed_at`, and on the dashboard that is the exact condition for showing the
	 * Welcome flow — so saving a tutor style bounced the student into first-run onboarding, name
	 * fields and AI greeting and all. Normalising at the boundary means no caller can repeat it.
	 */
	const profile = result && result.userProfile
	return Array.isArray(profile) ? profile[0] : profile
}

/*
 * The style a profile is currently set to, falling back to the first one.
 *
 * The fallback matters because it is the same rule the server uses: fetchUserProfile seeds a new
 * profile with `aiPersonalities[0].id`, and both sendMessage and fetchAiPersonalityForUser fall back
 * to `[0]` when the profile has no id set. So an unset profile is ALREADY being taught in the first
 * style — showing anything else here, or showing nothing, would misreport what the AI is doing.
 */
const currentTutorStyle = (styles, aiPersonalityId) => {
	if (!styles || styles.length === 0) return null
	return styles.find(s => s.id === aiPersonalityId) || styles[0]
}

export {TUTOR_STYLE_LABEL, fetchTutorStyles, saveTutorStyle, currentTutorStyle}
